import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isAgentEmploymentDateInputAllowed } from "@/utils/agentEmploymentDates";
import axios from "axios";
import { History, Loader2, Trash2, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const todayIso = () => new Date().toISOString().slice(0, 10);

const MANUAL_VALUE = "__manual__";

/** Takım Yönetimi kayıtlarından benzersiz lider listesi */
export function buildTeamLeadersFromTeams(teams, companyId) {
  const cid = companyId != null && companyId !== "" ? Number(companyId) : null;
  const map = new Map();
  (teams || []).forEach((t) => {
    if (cid && Number(t.company_id) !== cid) return;
    if (!t.lider_user_id) return;
    const id = Number(t.lider_user_id);
    const name = (t.lider_name || "").trim() || `Kullanıcı #${id}`;
    const teamLabel = (t.ad || "").trim();
    const prev = map.get(id);
    if (prev) {
      if (teamLabel && !prev.teamNames.includes(teamLabel)) {
        prev.teamNames.push(teamLabel);
      }
    } else {
      map.set(id, { id, name, teamNames: teamLabel ? [teamLabel] : [] });
    }
  });
  return [...map.values()]
    .map((l) => ({
      ...l,
      label: l.teamNames.length ? `${l.name} · ${l.teamNames.join(", ")}` : l.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "tr"));
}

const AgentTeamLeaderHistorySection = ({
  apiBase,
  agentId,
  companyId,
  teams = [],
  isOpen,
  isDark,
  labelClass,
  mutedClass,
  inputClass,
  pendingChange,
  onPendingChange,
}) => {
  const [history, setHistory] = useState([]);
  const [fetchedTeams, setFetchedTeams] = useState([]);
  const [apiLeaders, setApiLeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const leadersFromProps = useMemo(
    () => buildTeamLeadersFromTeams(teams, companyId),
    [teams, companyId],
  );

  const leadersFromFetch = useMemo(
    () => buildTeamLeadersFromTeams(fetchedTeams, companyId),
    [fetchedTeams, companyId],
  );

  const leaders = useMemo(() => {
    if (apiLeaders.length > 0) return apiLeaders;
    if (leadersFromProps.length > 0) return leadersFromProps;
    return leadersFromFetch;
  }, [apiLeaders, leadersFromProps, leadersFromFetch]);

  const selectValue = useMemo(() => {
    if (pendingChange.leader_user_id) return String(pendingChange.leader_user_id);
    if ((pendingChange.leader_name || "").trim() && !pendingChange.leader_user_id) return MANUAL_VALUE;
    return "__none__";
  }, [pendingChange.leader_user_id, pendingChange.leader_name]);

  const load = useCallback(async () => {
    if (!agentId || !isOpen) return;
    setLoading(true);
    try {
      const leaderParams = { action: "leaders" };
      if (companyId) leaderParams.company_id = companyId;
      const requests = [
        axios.get(`${apiBase}/agent-team-leader-history`, { params: { agent_id: agentId } }),
        axios.get(`${apiBase}/agent-team-leader-history`, { params: leaderParams }),
      ];
      if (companyId) {
        requests.push(axios.get(`${apiBase}/teams`, { params: { company_id: companyId } }));
      } else {
        requests.push(axios.get(`${apiBase}/teams`));
      }
      const [histRes, leadersRes, teamsRes] = await Promise.all(requests);
      setHistory(histRes.data?.history || []);
      setApiLeaders(Array.isArray(leadersRes.data?.leaders) ? leadersRes.data.leaders : []);
      setFetchedTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
    } catch {
      setHistory([]);
      setApiLeaders([]);
      setFetchedTeams([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, agentId, companyId, isOpen]);

  useEffect(() => { load(); }, [load]);

  const deleteHistoryRow = async (historyId) => {
    if (!historyId || !agentId) return;
    if (!window.confirm("Bu önceki takım lideri kaydı silinsin mi?")) return;
    setDeletingId(historyId);
    try {
      await axios.delete(`${apiBase}/agent-team-leader-history?id=${historyId}`);
      toast.success("Kayıt silindi");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    } finally {
      setDeletingId(null);
    }
  };

  const onLeaderSelect = (value) => {
    if (value === "__none__") {
      onPendingChange({ leader_user_id: null, leader_name: "", change_date: pendingChange.change_date || "" });
      return;
    }
    if (value === MANUAL_VALUE) {
      onPendingChange({
        leader_user_id: null,
        leader_name: pendingChange.leader_name || "",
        change_date: pendingChange.change_date || todayIso(),
      });
      return;
    }
    const id = Number(value);
    const picked = leaders.find((l) => l.id === id);
    onPendingChange({
      leader_user_id: id,
      leader_name: picked?.name || "",
      change_date: pendingChange.change_date || todayIso(),
    });
  };

  const cardClass = isDark ? "rounded-xl border border-slate-800 bg-slate-950/40 p-4 space-y-3" : "rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-3";

  return (
    <div className={cardClass}>
      <p className={`text-sm font-semibold flex items-center gap-2 ${isDark ? "text-slate-200" : "text-gray-800"}`}>
        <History className="w-4 h-4 text-indigo-400" />
        Önceki takım liderleri
      </p>
      <p className={`text-[11px] ${mutedClass}`}>
        Aktif ve pasif takım liderleri listelenir (pasif takım / pasif kullanıcı). Silinmiş lider için «Elle yaz» ile ad soyad girebilirsiniz.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 space-y-2">
          <Label className={labelClass}>Önceki takım lideri</Label>
          <Select value={selectValue} onValueChange={onLeaderSelect}>
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder="Takım lideri seçin" />
            </SelectTrigger>
            <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
              <SelectItem value="__none__">— Seçilmedi —</SelectItem>
              {leaders.map((l) => (
                <SelectItem key={l.id} value={String(l.id)}>{l.label}</SelectItem>
              ))}
              <SelectItem value={MANUAL_VALUE}>Elle ad soyad yaz…</SelectItem>
            </SelectContent>
          </Select>
          {(selectValue === MANUAL_VALUE || (pendingChange.leader_name && !pendingChange.leader_user_id)) && (
            <Input
              className={inputClass}
              placeholder="Örn. eski takım lideri adı soyadı"
              value={pendingChange.leader_name || ""}
              onChange={(e) => onPendingChange({
                ...pendingChange,
                leader_user_id: null,
                leader_name: e.target.value,
                change_date: pendingChange.change_date || (e.target.value.trim() ? todayIso() : ""),
              })}
            />
          )}
          {!loading && leaders.length === 0 && selectValue !== MANUAL_VALUE && (
            <p className={`text-[11px] ${isDark ? "text-amber-300" : "text-amber-700"}`}>
              Bu firma için takımda atanmış lider yok — «Elle ad soyad yaz» kullanın veya Takım Yönetiminden lider atayın.
            </p>
          )}
        </div>
        <div className="sm:col-span-2 sm:col-start-1 sm:max-w-xs">
          <Label className={labelClass}>Takım değişiklik tarihi</Label>
          <Input
            type="date"
            className={`mt-1.5 ${inputClass}`}
            value={pendingChange.change_date || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v !== "" && !isAgentEmploymentDateInputAllowed(v)) return;
              onPendingChange({ ...pendingChange, change_date: v });
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className={`flex items-center gap-2 text-xs ${mutedClass}`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Arşiv yükleniyor…
        </div>
      ) : history.length === 0 ? (
        <p className={`text-xs ${mutedClass}`}>Henüz kayıtlı önceki takım lideri yok.</p>
      ) : (
        <ul className={`text-xs space-y-1.5 max-h-40 overflow-y-auto border rounded-lg p-2 ${isDark ? "border-slate-800" : "border-gray-200"}`}>
          {history.map((h) => (
            <li key={h.id} className={`flex flex-wrap items-center justify-between gap-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
              <span className="inline-flex items-center gap-1.5 font-medium min-w-0">
                <UserCircle2 className="w-3.5 h-3.5 opacity-70 shrink-0" />
                <span className="truncate">{h.leader_name}</span>
                {!h.leader_user_id ? (
                  <span className={`text-[10px] ${mutedClass}`}>(elle)</span>
                ) : null}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`tabular-nums ${mutedClass}`}>{h.change_date}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={`h-7 w-7 ${isDark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50"}`}
                  title="Kaydı sil"
                  disabled={deletingId === h.id}
                  onClick={() => deleteHistoryRow(h.id)}
                >
                  {deletingId === h.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AgentTeamLeaderHistorySection;
